import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
// styled compnents
import "../../App.css";
import "./scss/Login.scss";
// form
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
// hooks
import { useDispatch, useSelector } from "react-redux";
import { loginAction } from "../../redux/features/auth";
import useCallApiAndLoad from "../../hooks/useCallApiAndLoad";
import { loginEndpoint } from "./services/loginService";
// adapters/helpers
import { getAuthUserAdapter } from "../../adapters/userAdapter";
import { fetchResponseAdapter } from "../../adapters/fetchAdapter";
import {
  customDelay,
  getLastPath,
  removeLastPath,
  setAuth,
} from "../../helpers/authHelper";
import { nextAuthUrl } from "../../helpers/nextApi";
// assets
import { LoginForm } from "./models/loginModel";
import FormControlError from "../../components/Controls/FormControlError";
import { mapApiCodeMessage } from "./helpers/responseHelper";
import { AppStore } from "../../redux/store";
import { IconLock, IconMail, IconMailCheck } from "@tabler/icons-react";
import MainButton from "../../components/MainButton/mainButton";
import VerticalSlider from "./components/SliderVertical/SliderVertical";
import Button from "../../components/form/Button";
import { useGoogleLogin } from "@react-oauth/google";
import { getCustomerInfo } from "../../services/accountService";
import { CustomerData } from "../../models/authUserModel";


const Login = () => {
  const { country } = useSelector((state: AppStore) => state.country);
  const [loginError, setLoginError] = useState("");
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: yupResolver(
      yup
        .object({
          username: yup.string().required("Usuario es requerido"),
          password: yup.string().required("Contraseña es requerida"),
        })
        .required()
    ),
  });

  // Función común para procesar respuesta exitosa de login
  const processSuccessfulLogin = async(responseData: any, loginType: string) => {
    
    const authData = getAuthUserAdapter({
      first_name: responseData.name,
      access_token: responseData.access_token,
      refresh_token: responseData.refresh_token,
      realm: responseData.realm,
      avatar: responseData.avatar,
      name: responseData.name,
      last_name: responseData.last_name,
      is_phone_verified: responseData.is_phone_verified
    });

    const token = responseData.access_token;
    if (token) {
      const { status, data } = await callEndpoint(getCustomerInfo(token));

   
      if (status === 200) {
        const response = fetchResponseAdapter(data);
        const customerData: CustomerData = response?.data;

        if (window.mixpanel?.people) {
          window.mixpanel.identify(customerData?.id as string)

          window.mixpanel.people.set({
            $email: customerData.email,
            $first_name: customerData.first_name,
            $last_name: customerData.last_name,
            $phone: customerData.area_code + customerData?.phone,
          })

          window.mixpanel.track(`User Login by ${loginType}`)
        }
      }
      
    }
    setTimeout(() => {
      dispatch(loginAction(authData))
      setAuth(authData)
      window.open(`/app/${country}/l/opciones/mi-cuenta`, '_self')
    }, 500)

  };

  // Función común para procesar errores de login
  const processLoginError = (data: any, loginType: string) => {
    const { code, message } = data;
    const messageDictionary = mapApiCodeMessage(code);
    // console.log({ message, messageDictionary });
    setLoginError(`Error ${loginType} (${code}): ${message || messageDictionary}`);
  };

  const doLogin = async (props: LoginForm) => {
    setLoginError("");
    const { username, password } = props;
    const { status, data } = await callEndpoint(
      loginEndpoint(username, password)
    );
    if (status === 200) {
      const response = fetchResponseAdapter(data);
      const responseData = response.data;
      processSuccessfulLogin(responseData, "Normal Login");
    } else {
      processLoginError(data, "");
    }
  };

  const goToCodeAccess = () => {
    window.open(`/app/${country}/auth/request-login-by-code`, "_self");
  };

  // Función para login con Google: pega a Next, que setea cookies httpOnly
  // (propi_session/refresh/user_data) y devuelve { user } sin tokens.
  const doGoogleLogin = async (googleAccessToken: string) => {
    setLoginError("");
    try {
      const { data } = await axios.post(
        nextAuthUrl(country, "google/login"),
        { google_access_token: googleAccessToken },
        { withCredentials: true }
      );
      const u = data?.user;
      if (!u) {
        processLoginError({ code: 502, message: "Respuesta inválida" }, "Google Login");
        return;
      }
      // Hidratamos Redux con la forma AuthUser. Token/rft quedan vacíos —
      // viven en cookies httpOnly y los services customer pasan por el proxy
      // de Next que las inyecta como Bearer.
      const authData = getAuthUserAdapter({
        first_name: u.first_name || u.name,
        access_token: "",
        refresh_token: "",
        realm: u.realm || u.info?.realm || "customer",
        avatar: u.avatar,
        name: u.name,
        last_name: u.last_name,
        is_phone_verified: u.info?.is_phone_verified,
      });

      // Mixpanel identify usa los datos enriquecidos que ya vienen en u.info
      // (Next ya pegó a /customer/info al loguear).
      const info = u.info || {};
      if (window.mixpanel?.people && info.id) {
        window.mixpanel.identify(info.id as string);
        window.mixpanel.people.set({
          $email: info.email,
          $first_name: info.first_name,
          $last_name: info.last_name,
          $phone: `${info.area_code || ""}${info.phone || ""}`,
        });
        window.mixpanel.track(`User Login by Google Direct`);
      }

      setTimeout(() => {
        dispatch(loginAction(authData));
        // No escribimos localStorage['auth']: la sesión vive en la cookie de
        // Next y el AppRouter rehidrata vía /api/auth/session al refresh.
        window.open(`/app/${country}/l/opciones/mi-cuenta`, "_self");
      }, 500);
    } catch (err: any) {
      const status = err?.response?.status || 0;
      const payload = err?.response?.data || {};
      processLoginError(
        { code: payload.code || status, message: payload.message || "Error de red" },
        "Google Login"
      );
    }
  };

  // Función de login con Google
  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      console.log("Google Access Token:", codeResponse.access_token);
      // Llamar a la función de Google login con el token obtenido
      doGoogleLogin(codeResponse.access_token);
    },
    onError: (error) => {
      console.log("Error en Google Login:", error);
      setLoginError("Error al autenticar con Google. Inténtalo de nuevo.");
    },
  });

  // Wrapper para manejar el click del botón
  const handleGoogleLogin = () => {
    googleLogin();
  };

  return (
    <div className="flex flex-col h-screen">
      <div className=" flex-1">
        <div className="flex lg:flex-row flex-col h-full">
          <div className="bg-[#0A1C13] lg:w-4/6 px-4 py-8 lg:py-0 lg:px-0 h-full lg:h-auto flex justify-center items-center lg:block">
            <div className="w-full flex flex-wrap bg-white lg:rounded-r-xl drop-shadow-none lg:h-full">
              <div className="w-full lg:w-[600px] m-auto px-8 py-10 rounded-lg container-form ">
                <div className="mb-2">
                  <a href={`/${country}`}>
                    <img
                      className="w-32"
                      src="https://storage.googleapis.com/assets-us-east4-propilatam/website-assets/propi-web/v2/logo-propi.svg"
                      alt=""
                    />
                  </a>
                </div>
                <p className="text-base text-[#757575] font-medium text-left leading-tight">
                  Accede fácilmente con:
                </p>
                <div className=" mt-4">
                  <Button
                    variant="outlined"
                    size="lg"
                    className="w-full bg-white hover:bg-gray-50 text-[#212121] border border-gray-300 text-sm lg:text-base"
                    startIcon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    }
                    label="Continuar con Google"
                    onClick={handleGoogleLogin}
                    disabled={isLoading}
                  />
                </div>
                {/* <div className="mt-3">
                  <Button
                    variant="outlined"
                    size="lg"
                    className="w-full bg-white hover:bg-gray-50 text-[#212121] border border-gray-300"
                    startIcon={
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z" fill="#000" />
                        <path d="M15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701z" fill="#000" />
                      </svg>
                    }
                    label="Continuar con Apple"
                    onClick={() => console.log("Apple login clicked")}
                    disabled={isLoading}
                  />
                </div> */}
                <div className="mt-3">
                  <Button
                    variant="outlined"
                    size="lg"
                    className="w-full bg-white hover:bg-gray-50 text-[#757575] border border-gray-300 text-sm lg:text-base"
                    startIcon={<IconMailCheck color="#757575" />}
                    label="Recibir código de acceso"
                    onClick={() => goToCodeAccess()}
                    disabled={isLoading}
                  />
                </div>

                <div className="divider-login">
                  <span className="text-sm font-medium text-[#212121] ">
                    O inicia sesión con tus datos
                  </span>
                </div>

                <form
                  id="inicio-de-sesion"
                  onSubmit={handleSubmit(doLogin)}
                  className="login-form mt-3"
                >
                  <div className="relative input-icon-login">
                    <IconMail color="#BDBDBD" />
                    <input
                      type="text"
                      className="w-full focus:outline-none mt-1 border border-[#BDBDBD] rounded-md text-[#212121] py-3 px-2 pl-10"
                      placeholder="Correo electronico"
                      {...register("username")}
                    />
                  </div>

                  <FormControlError
                    message={errors.username?.message}
                    additionalClass="mb-3"
                  />
                  <div className="relative input-icon-login">
                    <IconLock color="#BDBDBD" />
                    <input
                      type="password"
                      className="w-full focus:outline-none mt-1 border border-[#BDBDBD] rounded-md text-[#212121] py-3 px-2 pl-10"
                      placeholder="Contraseña"
                      {...register("password")}
                    />
                  </div>

                  <FormControlError
                    message={errors.password?.message}
                    additionalClass="mb-3"
                  />
                  <FormControlError
                    message={loginError}
                    additionalClass="mb-3"
                  />
                  <div className="my-4">
                    <Button
                      as="link"
                      href={`/app/${country}/auth/request-change-password`}
                      variant="transparent"
                      size="base"
                      className="!p-1 text-primary font-semibold text-left text-sm !justify-start !w-[fit-content]"
                      label="¿Olvidaste tu contraseña?"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="my-6">
                    <Button
                      type="submit"
                      variant="contained"
                      size="lg"
                      className="w-full"
                      label="Iniciar sesión"
                      loading={isLoading}
                    />
                  </div>
                  <div className="mt-2 text-center">
                    <span className="text-base text-[#757575] font-medium">
                      ¿No tienes una cuenta?{" "}
                    </span>
                    <Button
                      as="link"
                      href={`/app/${country}/auth/registrarse`}
                      variant="transparent"
                      size="base"
                      className="!p-1 text-primary font-semibold text-base !inline-flex"
                      label="Regístrate"
                      disabled={isLoading}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="hidden lg:flex flex-col lg:w-2/6 justify-center items-center text-flat-dark-blue-500 bg-gradient-to-t from-[rgba(3,35,18,1)] to-[rgba(0,182,107,1)]">
            <div className="w-full lg:p-0">
              <VerticalSlider />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
